import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import EngineMarkdown from '@modules/ui/markdown/components/EngineMarkdown';
import { ApiTag } from '@modules/ui/tags/types';
import { EnumReferenceItem } from '../../types';
import ReferenceMemberTags from '../../sharedUI/components/ReferenceMemberTags';
import { hasDeprecatedTag } from '../../sharedUI/utils/tagUtils';
import useEnumItemsTableStyles from './EnumItemsTable.styles';

interface EnumItemsTableProps {
  items: EnumReferenceItem[];
}

function EnumItemsTable({ items }: EnumItemsTableProps) {
  const { translate } = useTranslation();
  const { classes, cx } = useEnumItemsTableStyles();

  if (!items || items.length === 0) {
    return null;
  }

  const hasSummary = items.some(
    (item) => item.summary || item.deprecationMessage || (item.tags && item.tags.length > 0),
  );

  return (
    <div className={classes.container}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>
              <Typography variant='body2' className={classes.headerText}>
                {translate('Label.Name') || 'Name'}
              </Typography>
            </TableCell>
            <TableCell className={classes.headerCell}>
              <Typography variant='body2' className={classes.headerText}>
                {translate('Label.Value') || 'Value'}
              </Typography>
            </TableCell>
            {hasSummary && (
              <TableCell className={classes.headerCell}>
                <Typography variant='body2' className={classes.headerText}>
                  {translate('Label.Summary') || 'Summary'}
                </Typography>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const isDeprecated = hasDeprecatedTag(item.tags ?? null);
            return (
              <TableRow key={item.name} className={classes.tableRow}>
                <TableCell>
                  <Typography
                    variant='body2'
                    className={cx(classes.nameText, {
                      [classes.deprecatedText]: isDeprecated,
                    })}>
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant='body2'
                    className={cx(classes.valueText, {
                      [classes.deprecatedText]: isDeprecated,
                    })}>
                    {item.value}
                  </Typography>
                </TableCell>
                {hasSummary && (
                  <TableCell>
                    {isDeprecated ? (
                      <ReferenceMemberTags
                        memberName={item.name}
                        tags={[ApiTag.Deprecated]}
                        engineRevampEnabled
                      />
                    ) : (
                      item.summary && (
                        <EngineMarkdown overrideParagraphStyle={{ margin: 0 }}>
                          {item.summary}
                        </EngineMarkdown>
                      )
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default EnumItemsTable;
